// client/ui — secret: parchment HUD, inventory grid, equip slots, market panel.
// Vanilla DOM/CSS per plan §4. Phase 0 implements only HUD + notify.

const root = (): HTMLElement => {
  const el = document.getElementById('ui-root');
  if (!el) throw new Error('client/ui: #ui-root missing in index.html');
  return el;
};

let hud: HTMLElement | null = null;

function ensureHud(): HTMLElement {
  if (hud) return hud;
  hud = document.createElement('div');
  hud.className = 'hud parchment';
  hud.style.cssText = `
    position: absolute;
    top: 18px;
    left: 18px;
    padding: 14px 22px;
    font-size: 0.95rem;
    line-height: 1.55;
    min-width: 230px;
    white-space: pre-line;
    font-weight: 500;
  `;
  root().appendChild(hud);
  return hud;
}

export function setHudText(text: string): void {
  ensureHud().textContent = text;
}

let notificationStack: HTMLElement | null = null;
function ensureNotificationStack(): HTMLElement {
  if (notificationStack) return notificationStack;
  notificationStack = document.createElement('div');
  notificationStack.style.cssText = `
    position: absolute;
    top: 18px;
    right: 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
    pointer-events: none;
  `;
  root().appendChild(notificationStack);
  return notificationStack;
}

export function notify(msg: string, durationMs = 3000): void {
  const stack = ensureNotificationStack();
  const n = document.createElement('div');
  n.className = 'panel parchment';
  n.textContent = msg;
  n.style.cssText = `
    padding: 12px 18px;
    font-size: 0.95rem;
    transition: opacity 200ms;
    min-width: 200px;
    text-align: center;
  `;
  stack.appendChild(n);
  setTimeout(() => {
    n.style.opacity = '0';
    setTimeout(() => n.remove(), 200);
  }, durationMs);
}

export function hideBootOverlay(): void {
  const overlay = document.getElementById('boot-overlay');
  if (overlay) overlay.classList.add('hidden');
}

let craftPrompt: HTMLElement | null = null;
function ensureCraftPrompt(): HTMLElement {
  if (craftPrompt) return craftPrompt;
  craftPrompt = document.createElement('div');
  craftPrompt.className = 'panel parchment';
  craftPrompt.style.cssText = `
    position: absolute;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 14px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    display: none;
    pointer-events: none;
  `;
  root().appendChild(craftPrompt);
  return craftPrompt;
}

export function setCraftPrompt(label: string | null): void {
  const el = ensureCraftPrompt();
  if (label) {
    el.textContent = label;
    el.style.display = 'block';
  } else {
    el.style.display = 'none';
  }
}

export function openPanel(_name: string): void {
  // Phase 2+ will implement.
}

export function bindHotkey(_key: string, _action: () => void): void {
  // Phase 2+ will implement.
}

// Floating world-space bar (HP, cast, etc.)
export interface FloatingBar {
  el: HTMLElement;
  update(opts: {
    screenX: number;
    screenY: number;
    fillPct: number;
    visible: boolean;
    label?: string;
    color?: string;
  }): void;
  destroy(): void;
}

export function createFloatingBar(initialColor = '#c83030'): FloatingBar {
  const el = document.createElement('div');
  el.style.cssText = `
    position: absolute;
    width: 80px;
    height: 8px;
    transform: translate(-50%, -100%);
    background: rgba(0,0,0,0.65);
    border: 1px solid #2a1810;
    border-radius: 2px;
    pointer-events: none;
    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
  `;
  const fill = document.createElement('div');
  fill.style.cssText = `
    height: 100%;
    width: 100%;
    background: ${initialColor};
    transition: width 120ms;
  `;
  el.appendChild(fill);

  const label = document.createElement('div');
  label.style.cssText = `
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: #f4e9d0;
    text-shadow: 0 1px 3px #000, 0 0 2px #000;
    white-space: nowrap;
  `;
  el.appendChild(label);

  root().appendChild(el);

  return {
    el,
    update(opts): void {
      el.style.display = opts.visible ? 'block' : 'none';
      if (!opts.visible) return;
      el.style.left = `${opts.screenX}px`;
      el.style.top = `${opts.screenY}px`;
      fill.style.width = `${Math.max(0, Math.min(100, opts.fillPct * 100))}%`;
      if (opts.color) fill.style.background = opts.color;
      label.textContent = opts.label ?? '';
    },
    destroy(): void {
      el.remove();
    },
  };
}

// Inventory + equipment panel
export interface InventoryRow {
  itemId: string;
  defId: string;
  name: string;
  iconColor: string;
  count: number;
  description: string;
  equippableSlot: 'mainHand' | 'offHand' | 'head' | 'chest' | 'shoes' | 'cape' | null;
  isEquipped: boolean;
  tier: number;
  quality: string;
  // Pre-formatted multi-line "current → new" diff vs currently-equipped item in this slot.
  // null for non-equippables, or when equipping would change nothing.
  statDelta: string | null;
}

export interface EquipmentSlotRow {
  slot: 'mainHand' | 'offHand' | 'head' | 'chest' | 'shoes' | 'cape';
  label: string;
  defId: string | null;
  name: string | null;
  iconColor: string | null;
}

export interface InventoryPanelHandlers {
  onSlotClick(itemId: string): void;
  onEquipSlotClick(slot: EquipmentSlotRow['slot']): void;
}

export interface InventoryPanel {
  setOpen(open: boolean): void;
  toggle(): void;
  isOpen(): boolean;
  setRows(rows: InventoryRow[]): void;
  setEquipment(eq: EquipmentSlotRow[]): void;
}

export function createInventoryPanel(handlers: InventoryPanelHandlers): InventoryPanel {
  const panel = document.createElement('div');
  panel.className = 'panel parchment';
  panel.style.cssText = `
    position: absolute;
    top: 50%;
    right: 22px;
    transform: translateY(-50%);
    padding: 16px 20px;
    width: 380px;
    display: none;
    flex-direction: column;
    gap: 14px;
    max-height: 80vh;
    overflow-y: auto;
  `;
  const title = document.createElement('div');
  title.textContent = 'Inventory & Equipment';
  title.style.cssText = `
    font-size: 1.1rem;
    font-weight: 700;
    text-align: center;
    border-bottom: 1px solid #5a3a22;
    padding-bottom: 6px;
    letter-spacing: 0.05em;
  `;
  panel.appendChild(title);

  const equipSection = document.createElement('div');
  equipSection.style.cssText = `display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;`;
  panel.appendChild(equipSection);

  const invHeader = document.createElement('div');
  invHeader.textContent = 'Carried';
  invHeader.style.cssText = `font-size: 0.85rem; opacity: 0.8; margin-top: 4px;`;
  panel.appendChild(invHeader);

  const invGrid = document.createElement('div');
  invGrid.style.cssText = `
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 4px;
  `;
  panel.appendChild(invGrid);

  const tooltip = document.createElement('div');
  tooltip.style.cssText = `
    position: fixed;
    pointer-events: none;
    padding: 8px 10px;
    background: rgba(20, 14, 8, 0.95);
    color: #f4e9d0;
    font-size: 0.85rem;
    border-radius: 4px;
    border: 1px solid #c0a070;
    max-width: 220px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.5);
    line-height: 1.35;
    z-index: 1000;
    display: none;
    white-space: pre-wrap;
  `;
  document.body.appendChild(tooltip);

  function showTooltip(html: string, e: MouseEvent): void {
    tooltip.innerHTML = html;
    tooltip.style.display = 'block';
    tooltip.style.left = `${Math.min(e.clientX + 14, window.innerWidth - 240)}px`;
    tooltip.style.top = `${Math.min(e.clientY + 14, window.innerHeight - 100)}px`;
  }
  function hideTooltip(): void {
    tooltip.style.display = 'none';
  }

  let open = false;
  root().appendChild(panel);

  function makeTile(opts: {
    iconColor: string | null;
    label: string;
    sub?: string;
    onClick?: () => void;
    tooltip?: string;
    highlight?: boolean;
  }): HTMLElement {
    const tile = document.createElement('div');
    tile.style.cssText = `
      position: relative;
      height: 56px;
      background: ${opts.iconColor ?? '#3a2a1c'};
      border: 2px solid ${opts.highlight ? '#f0d070' : '#2a1810'};
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px #c0a070, ${opts.highlight ? '0 0 12px 2px #f0d070' : 'none'};
      cursor: ${opts.onClick ? 'pointer' : 'default'};
      display: flex;
      align-items: flex-end;
      justify-content: center;
      padding: 4px;
      overflow: hidden;
    `;
    const lbl = document.createElement('div');
    lbl.textContent = opts.label;
    lbl.style.cssText = `
      font-size: 9px;
      color: #f4e9d0;
      text-shadow: 0 1px 2px #000;
      letter-spacing: 0.02em;
      text-align: center;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `;
    tile.appendChild(lbl);
    if (opts.sub) {
      const sub = document.createElement('div');
      sub.textContent = opts.sub;
      sub.style.cssText = `
        position: absolute;
        top: 2px;
        right: 4px;
        font-size: 10px;
        color: #f4e9d0;
        text-shadow: 0 1px 2px #000;
        font-weight: 700;
      `;
      tile.appendChild(sub);
    }
    if (opts.onClick) {
      tile.addEventListener('click', opts.onClick);
    }
    if (opts.tooltip) {
      tile.addEventListener('mouseenter', (e) => showTooltip(opts.tooltip!, e as MouseEvent));
      tile.addEventListener('mousemove', (e) => showTooltip(opts.tooltip!, e as MouseEvent));
      tile.addEventListener('mouseleave', hideTooltip);
    }
    return tile;
  }

  function renderEquip(eq: EquipmentSlotRow[]): void {
    equipSection.innerHTML = '';
    for (const e of eq) {
      const tip = e.defId
        ? `<b>${e.name}</b>\n<i>Equipped</i>\nClick to unequip.`
        : `<b>${e.label}</b>\n<i>Empty</i>\nClick a matching item below to equip.`;
      const tile = makeTile({
        iconColor: e.iconColor ?? '#2e1f12',
        label: e.label,
        sub: e.defId ? '✓' : '',
        tooltip: tip,
        onClick: () => handlers.onEquipSlotClick(e.slot),
        highlight: !!e.defId,
      });
      tile.style.height = '64px';
      equipSection.appendChild(tile);
    }
  }

  function renderInv(rows: InventoryRow[]): void {
    invGrid.innerHTML = '';
    const SLOTS = 24;
    for (let i = 0; i < SLOTS; i++) {
      const r = rows[i];
      if (!r) {
        const empty = document.createElement('div');
        empty.style.cssText = `
          height: 56px;
          background: rgba(30, 22, 14, 0.4);
          border: 1px dashed rgba(90, 58, 34, 0.5);
          border-radius: 3px;
        `;
        invGrid.appendChild(empty);
        continue;
      }
      const tip = [
        `<b>${r.name}</b>`,
        `T${r.tier} · ${r.quality}`,
        r.equippableSlot ? `<i>Click to ${r.isEquipped ? 'unequip' : 'equip in ' + r.equippableSlot}</i>` : '<i>Material</i>',
        r.statDelta ? '' : null,
        r.statDelta,
        '',
        r.description,
      ].filter((x): x is string => x !== null).join('\n');
      const tile = makeTile({
        iconColor: r.iconColor,
        label: r.name,
        sub: r.count > 1 ? `×${r.count}` : '',
        tooltip: tip,
        onClick: () => handlers.onSlotClick(r.itemId),
        highlight: r.isEquipped,
      });
      invGrid.appendChild(tile);
    }
  }

  function setOpen(v: boolean): void {
    open = v;
    panel.style.display = v ? 'flex' : 'none';
    if (!v) hideTooltip();
  }

  return {
    setOpen,
    toggle(): void {
      setOpen(!open);
    },
    isOpen(): boolean {
      return open;
    },
    setRows: renderInv,
    setEquipment: renderEquip,
  };
}

// Crafting panel
export interface CraftingRecipeRow {
  recipeId: string;
  outputName: string;
  outputIconColor: string;
  outputDescription: string;
  outputTier: number;
  ingredients: ReadonlyArray<{ defId: string; name: string; iconColor: string; have: number; need: number }>;
  craftingFame: number;
  craftMs: number;
  affordable: boolean;
}

export interface CraftingPanel {
  setOpen(open: boolean): void;
  isOpen(): boolean;
  setRecipes(stationLabel: string, rows: CraftingRecipeRow[]): void;
}

export interface CraftingPanelHandlers {
  onCraft(recipeId: string): void;
  onClose(): void;
}

export function createCraftingPanel(handlers: CraftingPanelHandlers): CraftingPanel {
  const panel = document.createElement('div');
  panel.className = 'panel parchment';
  panel.style.cssText = `
    position: absolute;
    top: 50%;
    left: 22px;
    transform: translateY(-50%);
    padding: 16px 18px;
    width: 360px;
    display: none;
    flex-direction: column;
    gap: 8px;
    max-height: 80vh;
    overflow-y: auto;
  `;
  const header = document.createElement('div');
  header.style.cssText = `display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #5a3a22; padding-bottom: 6px;`;
  const title = document.createElement('div');
  title.style.cssText = `font-size: 1.05rem; font-weight: 700; letter-spacing: 0.05em;`;
  header.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.textContent = '✕';
  closeBtn.style.cssText = `
    background: transparent;
    border: 1px solid #5a3a22;
    color: #2a1810;
    width: 24px;
    height: 24px;
    border-radius: 3px;
    cursor: pointer;
    font-weight: 700;
  `;
  closeBtn.addEventListener('click', () => handlers.onClose());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const list = document.createElement('div');
  list.style.cssText = `display: flex; flex-direction: column; gap: 6px;`;
  panel.appendChild(list);

  let open = false;
  root().appendChild(panel);

  function setOpen(v: boolean): void {
    open = v;
    panel.style.display = v ? 'flex' : 'none';
  }

  function setRecipes(stationLabel: string, rows: CraftingRecipeRow[]): void {
    title.textContent = stationLabel;
    list.innerHTML = '';
    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No recipes here.';
      empty.style.cssText = 'font-style: italic; opacity: 0.7; padding: 8px;';
      list.appendChild(empty);
      return;
    }
    for (const r of rows) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: grid;
        grid-template-columns: 36px 1fr auto;
        gap: 10px;
        align-items: center;
        padding: 8px;
        background: rgba(36, 24, 14, 0.18);
        border: 1px solid rgba(90, 58, 34, 0.3);
        border-radius: 4px;
      `;
      const swatch = document.createElement('div');
      swatch.style.cssText = `width: 36px; height: 36px; background: ${r.outputIconColor}; border: 2px solid #2a1810; border-radius: 3px; box-shadow: inset 0 0 0 1px #c0a070;`;
      row.appendChild(swatch);

      const info = document.createElement('div');
      const nameEl = document.createElement('div');
      nameEl.textContent = `${r.outputName} (T${r.outputTier})`;
      nameEl.style.cssText = 'font-weight: 700; font-size: 0.92rem;';
      info.appendChild(nameEl);

      const ing = document.createElement('div');
      ing.style.cssText = 'font-size: 0.8rem; color: #2a1810; line-height: 1.3;';
      ing.innerHTML = r.ingredients
        .map((i) => {
          const ok = i.have >= i.need;
          return `<span style="color:${ok ? '#225522' : '#882020'}">${i.name} ${i.have}/${i.need}</span>`;
        })
        .join(' · ');
      info.appendChild(ing);

      const fame = document.createElement('div');
      fame.textContent = `+${r.craftingFame} fame · ${(r.craftMs / 1000).toFixed(1)}s`;
      fame.style.cssText = 'font-size: 0.7rem; opacity: 0.7;';
      info.appendChild(fame);
      row.appendChild(info);

      const btn = document.createElement('button');
      btn.textContent = 'Craft';
      btn.disabled = !r.affordable;
      btn.style.cssText = `
        background: ${r.affordable ? '#3a6a3a' : '#5a4a3a'};
        color: #f4e9d0;
        border: 1px solid #2a1810;
        border-radius: 3px;
        padding: 6px 12px;
        cursor: ${r.affordable ? 'pointer' : 'not-allowed'};
        font-weight: 700;
        font-family: 'Palatino Linotype', 'Georgia', serif;
      `;
      btn.addEventListener('click', () => {
        if (r.affordable) handlers.onCraft(r.recipeId);
      });
      row.appendChild(btn);

      list.appendChild(row);
    }
  }

  return {
    setOpen,
    isOpen: () => open,
    setRecipes,
  };
}

// Gather progress bar (single, top-center)
export interface GatherBar {
  show(label: string): void;
  setProgress(pct: number): void;
  hide(): void;
}

export function createGatherBar(): GatherBar {
  const wrap = document.createElement('div');
  wrap.className = 'panel parchment';
  wrap.style.cssText = `
    position: absolute;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 14px;
    width: 240px;
    display: none;
    flex-direction: column;
    gap: 4px;
    align-items: center;
  `;
  const label = document.createElement('div');
  label.style.cssText = 'font-size: 0.85rem; font-weight: 700;';
  wrap.appendChild(label);
  const track = document.createElement('div');
  track.style.cssText = `
    width: 100%;
    height: 8px;
    background: rgba(20, 14, 8, 0.6);
    border: 1px solid #2a1810;
    border-radius: 2px;
    overflow: hidden;
  `;
  const fill = document.createElement('div');
  fill.style.cssText = `height: 100%; width: 0%; background: #c0a070; transition: width 100ms linear;`;
  track.appendChild(fill);
  wrap.appendChild(track);
  root().appendChild(wrap);

  return {
    show(text: string): void {
      label.textContent = text;
      wrap.style.display = 'flex';
      fill.style.width = '0%';
    },
    setProgress(pct: number): void {
      fill.style.width = `${Math.max(0, Math.min(100, pct * 100))}%`;
    },
    hide(): void {
      wrap.style.display = 'none';
    },
  };
}

// Floating damage number that drifts upward and fades.
export function spawnDamageNumber(screenX: number, screenY: number, amount: number, kind: 'hit' | 'crit' | 'heal' = 'hit'): void {
  const el = document.createElement('div');
  el.textContent = kind === 'heal' ? `+${amount}` : `${amount}`;
  const color = kind === 'crit' ? '#ffd060' : kind === 'heal' ? '#80e090' : '#ff6868';
  el.style.cssText = `
    position: absolute;
    left: ${screenX}px;
    top: ${screenY}px;
    transform: translate(-50%, -100%);
    color: ${color};
    font-weight: 800;
    font-size: ${kind === 'crit' ? '1.5rem' : '1.15rem'};
    text-shadow: 0 2px 6px #000, 0 0 4px #000;
    pointer-events: none;
    transition: transform 700ms cubic-bezier(.2,.8,.4,1), opacity 700ms;
    font-family: 'Palatino Linotype', 'Georgia', serif;
  `;
  root().appendChild(el);
  requestAnimationFrame(() => {
    el.style.transform = `translate(-50%, -260%)`;
    el.style.opacity = '0';
  });
  setTimeout(() => el.remove(), 720);
}
